import { Image as AntImage } from "antd";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { FileProviderFile } from "../lib/file-provider";
import { useFileProvider } from "../lib/hooks";

export type ImageResizeOptions = {
  w?: number;
  h?: number;
};

type ImageProps = {
  src?: string | null;
  file?: FileProviderFile;
  cloudKey?: string;
  width?: string | number;
  height?: string | number;
  fit: "fill" | "contain" | "cover" | "none";
  alt?: string;
  placeholder?: ReactNode;
  className?: string;
  loading?: "eager" | "lazy";
  resize?: ImageResizeOptions;
  /** use image preview (blob) if exists */
  usePreview?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
};

export const Image: React.FC<ImageProps> = ({
  fit,
  alt,
  file,
  height,
  width,
  src,
  cloudKey,
  placeholder,
  className,
  loading,
  resize,
  usePreview,
  onError,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onLoad,
}: ImageProps) => {
  const fileProvider = useFileProvider();
  const [success, setSuccess] = useState(true);

  const url = useMemo(() => {
    return (
      src ??
      (cloudKey
        ? fileProvider.getUrlByKey(cloudKey!)
        : fileProvider.getUrl(file!, usePreview ?? false))
    );
  }, [file, src, usePreview, cloudKey]);

  const thumbnailUrl = useMemo(() => {
    if (url?.startsWith("http") && resize) {
      try {
        const parsedUrl = new URL(url);
        if (resize.h) parsedUrl.searchParams.append("d", `h${resize.h}`);
        else if (resize.w) parsedUrl.searchParams.append("d", `w${resize.w}`);

        return parsedUrl.toString();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return url;
  }, [url, resize]);

  const handleError = useCallback(() => {
    setSuccess(false);
    onError?.();
  }, [onError]);

  const getImageStyle = useCallback(() => {
    return {
      width,
      height,
      objectFit: fit,
    };
  }, [width, height, fit]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if ((!success || !url) && placeholder) return <>{placeholder}</>;

  return (
    <AntImage
      loading={loading}
      src={thumbnailUrl}
      width={width}
      height={height}
      preview={
        usePreview
          ? {
              src: url,
            }
          : false
      }
      style={getImageStyle()}
      alt={alt}
      onLoad={() => onLoad?.()}
      onError={() => handleError()}
      onMouseEnter={() => onMouseEnter?.()}
      onMouseLeave={() => onMouseLeave?.()}
      className={className}
      onClick={() => onClick?.()}
    />
  );
};
